import { useCallback, useEffect, useRef, useState } from 'react';
import { getCentralDbUrls } from '../services/centralDbClient';

export type SyncEventType =
  | 'phase.start'
  | 'phase.progress'
  | 'phase.complete'
  | 'sync.complete'
  | 'heartbeat';

export interface SyncPhaseStartEvent {
  type: 'phase.start';
  syncRunId: string;
  phase: string;
  organizations: string[];
  timestamp: string;
}

export interface SyncPhaseProgressEvent {
  type: 'phase.progress';
  syncRunId: string;
  phase: string;
  organization: string;
  progress: {
    current: number;
    total: number;
    upserted?: number;
    errors?: number;
  };
  timestamp: string;
}

export interface SyncPhaseCompleteEvent {
  type: 'phase.complete';
  syncRunId: string;
  phase: string;
  result: 'SUCCESS' | 'FAILED' | 'PARTIAL';
  durationMs: number;
  summary: Record<string, any>;
  error?: string;
  timestamp: string;
}

export interface SyncCompleteEvent {
  type: 'sync.complete';
  syncRunId: string;
  success: boolean;
  durationMs: number;
  summary: Record<string, any>;
  error?: string;
  timestamp: string;
}

export interface HeartbeatEvent {
  type: 'heartbeat';
  syncRunId: string;
  timestamp: string;
}

export type SyncEvent =
  | SyncPhaseStartEvent
  | SyncPhaseProgressEvent
  | SyncPhaseCompleteEvent
  | SyncCompleteEvent
  | HeartbeatEvent;

export interface UseSyncEventStreamOptions {
  accessToken?: string | null;
  onEvent?: (event: SyncEvent) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export interface UseSyncEventStreamResult {
  events: SyncEvent[];
  connected: boolean;
  error: string | null;
  latestPhase: string | null;
  latestProgress: SyncPhaseProgressEvent | null;
  completed: boolean;
  disconnect: () => void;
}

/**
 * Hook for connecting to the sync events SSE stream.
 */
export function useSyncEventStream(
  syncRunId: string | null,
  options: UseSyncEventStreamOptions = {},
): UseSyncEventStreamResult {
  const { accessToken, onEvent, onError, onComplete } = options;

  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latestPhase, setLatestPhase] = useState<string | null>(null);
  const [latestProgress, setLatestProgress] = useState<SyncPhaseProgressEvent | null>(null);
  const [completed, setCompleted] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const currentSyncRunIdRef = useRef<string | null>(null);

  // Store callbacks in refs to avoid effect re-runs
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  const onCompleteRef = useRef(onComplete);
  onEventRef.current = onEvent;
  onErrorRef.current = onError;
  onCompleteRef.current = onComplete;

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('[SSE] Disconnecting');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    // If syncRunId hasn't changed, don't do anything
    if (currentSyncRunIdRef.current === syncRunId) {
      return;
    }

    // Close existing connection
    disconnect();

    // Update the ref
    currentSyncRunIdRef.current = syncRunId;

    // Reset state for new syncRunId
    setEvents([]);
    setError(null);
    setLatestPhase(null);
    setLatestProgress(null);
    setCompleted(false);
    setConnected(false);

    // If no syncRunId, we're done
    if (!syncRunId) {
      return;
    }

    // If no token, can't connect
    if (!accessToken) {
      console.warn('[SSE] No access token, cannot connect');
      setError('No access token');
      return;
    }

    const urls = getCentralDbUrls();
    const sseUrl = `${urls.apiBaseUrl}/easyflex/syncs/${encodeURIComponent(syncRunId)}/events?token=${encodeURIComponent(accessToken)}`;

    console.log('[SSE] Connecting to:', sseUrl.replace(/token=[^&]+/, 'token=***'));

    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;

    const handleEvent = (event: SyncEvent) => {
      if (event.type === 'heartbeat') {
        setConnected(true);
        return;
      }

      setEvents((prev) => [...prev, event]);
      onEventRef.current?.(event);

      if (event.type === 'phase.start' || event.type === 'phase.complete') {
        setLatestPhase(event.phase);
      }

      if (event.type === 'phase.progress') {
        setLatestProgress(event);
      }

      if (event.type === 'sync.complete') {
        setCompleted(true);
        setLatestPhase(null);
        setLatestProgress(null);
        onCompleteRef.current?.();
        setTimeout(() => {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
        }, 500);
      }
    };

    eventSource.onopen = () => {
      console.log('[SSE] Connection opened for syncRunId:', syncRunId);
      setConnected(true);
      setError(null);
    };

    eventSource.onerror = () => {
      console.error('[SSE] Connection error, readyState:', eventSource.readyState);
      setError('SSE connection error');
      setConnected(false);
      onErrorRef.current?.(new Error('SSE connection error'));
    };

    // Listen for typed events
    const eventTypes: SyncEventType[] = [
      'phase.start',
      'phase.progress',
      'phase.complete',
      'sync.complete',
      'heartbeat',
    ];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, (e: MessageEvent) => {
        try {
          const event: SyncEvent = JSON.parse(e.data);
          handleEvent(event);
        } catch (parseError) {
          console.warn('[SSE] Failed to parse event:', parseError);
        }
      });
    });

    // Also handle generic message events
    eventSource.onmessage = (e: MessageEvent) => {
      try {
        const event: SyncEvent = JSON.parse(e.data);
        handleEvent(event);
      } catch {
        // Ignore non-JSON messages
      }
    };

    return () => {
      console.log('[SSE] Cleanup for syncRunId:', syncRunId);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [syncRunId, accessToken, disconnect]);

  return {
    events,
    connected,
    error,
    latestPhase,
    latestProgress,
    completed,
    disconnect,
  };
}
